import Configuration from "@/configuration";
import axios from "axios";
import {ref} from "vue";

export function useDownload() {
    const openLoading = ref(false);
    const downloadLoading = ref(false);

    const getOS = () => {
        // 1. Modern approach (Chrome/Edge/Opera)
        // We use optional chaining (?.) to prevent errors if userAgentData doesn't exist
        const platform = navigator?.userAgentData?.platform;

        if (platform) {
            const p = platform.toLowerCase();
            if (p.includes("win")) return "windows";
            if (p.includes("mac")) return "mac";
            if (p.includes("linux")) return "linux";
        }

        // 2. Fallback approach (Firefox/Safari/Legacy)
        // userAgent is available in every browser ever made
        const ua = navigator.userAgent.toLowerCase();
        if (ua.includes("win")) return "windows";
        if (ua.includes("mac")) return "mac";
        if (ua.includes("linux")) return "linux";

        return "other";
    };

    function getOfficeProtocol(fileName) {
        if (!fileName) return null;

        const extension = fileName.split(".").pop().toLowerCase();

        const protocols = {
            // Word
            doc: "ms-word",
            docx: "ms-word",
            docm: "ms-word",
            dot: "ms-word",
            dotx: "ms-word",

            // Excel
            xls: "ms-excel",
            xlsx: "ms-excel",
            xlsm: "ms-excel",
            xlsb: "ms-excel",

            // PowerPoint
            ppt: "ms-powerpoint",
            pptx: "ms-powerpoint",
            pptm: "ms-powerpoint"
        };

        return protocols[extension] || null;
    }

    function downloadOrOpen(endPoint, id, fileName, open) {
        const ext = fileName.toLowerCase().split(".").pop();
        const isImage = ["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext);
        const isPdf = ext === "pdf";
        const officeProtocol = getOfficeProtocol(fileName);
        const os = getOS();

        if (open) {
            if ((os === "windows" || os === "mac") && officeProtocol) {
                openLoading.value = true;
                const absoluteApiUrl = `${Configuration.value("rootApi")}/api/${endPoint}/office/${id}/${fileName}`;
                window.location.href = `${officeProtocol}:ofe|u|${absoluteApiUrl}`;
                openLoading.value = false;
            } else if (os === "linux" || isPdf || isImage) {
                openLoading.value = true;
                axios.get(`/${endPoint}/download/${id}`, {responseType: "blob"}).then(response => {
                    const ext = fileName.toLowerCase().split(".").pop();

                    // 1. Manually map common extensions to correct MIME types
                    const mimeMap = {
                        pdf: "application/pdf",
                        jpg: "image/jpeg",
                        jpeg: "image/jpeg",
                        png: "image/png",
                        gif: "image/gif",
                        svg: "image/svg+xml"
                    };

                    const mimeType = mimeMap[ext] || response.headers["content-type"] || "application/octet-stream";

                    const blob = new Blob([response.data], {type: mimeType});
                    const url = URL.createObjectURL(blob);

                    const newWindow = window.open(url, "_blank");

                    if (!newWindow) {
                        alert("Popup blocked! Please allow popups for this site.");
                    }

                    setTimeout(() => URL.revokeObjectURL(url), 30000);
                }).finally(() => {
                    openLoading.value = false;
                });
            }
        } else {
            downloadLoading.value = true;
            axios.get(`/${endPoint}/download/${id}`, {responseType: "blob"}).then(response => {
                const url = URL.createObjectURL(response.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = fileName;
                link.click();
                URL.revokeObjectURL(url);
            }).finally(() => {
                downloadLoading.value = false;
            });
        }
    }

    function download(api, id, fileName) {
        downloadLoading.value = true;
        axios.get(`/${api}/download/${id}`, {responseType: "blob"}).then(response => {
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            link.click();
            // Revoke the temporary URL to free up memory
            URL.revokeObjectURL(url);
        }).finally(() => {
            downloadLoading.value = false;
        });
    }

    return {
        openLoading,
        downloadLoading,
        getOS,
        getOfficeProtocol,
        downloadOrOpen,
        download,
    }
}